import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import logo from '../../assets/justservicelogo.png';
import './Auth.css';

export default function Login() {
  const navigate = useNavigate();
  const { signup, loginWithEmail } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasReferral, setHasReferral] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Login fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPass, setShowLoginPass] = useState(false);

  // Register fields
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [showRegPass, setShowRegPass] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);
  const [referralCode, setReferralCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.includes('@')) { setError('Enter valid email address'); return; }
    if (loginPassword.length < 6) { setError('Enter your password'); return; }
    setLoading(true); setError('');
    try {
      await loginWithEmail(loginEmail, loginPassword);
      toast.success('Logged in successfully!');
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch {
      setForgotSent(true); // always show success to prevent email enumeration
    } finally {
      setForgotLoading(false);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])/.test(password)) return 'Password must contain lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain uppercase letter';
    if (!/(?=.*\d)/.test(password)) return 'Password must contain a number';
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regName.trim()) { setError('Enter your full name'); return; }
    if (!regEmail.includes('@')) { setError('Enter valid email address'); return; }
    if (regPhone.length !== 10) { setError('Enter valid 10-digit mobile number'); return; }
    const passwordError = validatePassword(regPassword);
    if (passwordError) { setError(passwordError); return; }
    if (regPassword !== regConfirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      await signup(regName, regEmail, regPhone, regPassword, referralCode);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-split">
      <div className="auth-split-card">

        {/* ── Left Side ── */}
        <div className="auth-left">
          <div className="auth-left-logo-wrap">
            <img src={logo} alt="JustService24" className="auth-logo-img-sm" />
            <div>
              <div className="auth-brand-name">Just<span>Service24</span></div>
              <div className="auth-brand-tagline">Your Local Business Directory</div>
            </div>
          </div>

          <div className="auth-left-body">
            <h2 className="auth-welcome-title">Welcome to JustService24!</h2>
            <p className="auth-welcome-desc">
              As you log in, you're opening the door to a world of convenience and innovation.
            </p>
            <div className="auth-illustration">
              <div className="auth-illus-building">
                <div className="illus-roof" />
                <div className="illus-body">
                  <div className="illus-door" />
                  <div className="illus-window" />
                  <div className="illus-window" />
                </div>
              </div>
              <div className="auth-illus-dots">
                <span /><span /><span />
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Side ── */}
        <div className="auth-right">
          <div className="auth-right-inner">
            <div className="auth-right-header">
              <h3>Welcome Back</h3>
              <p>{forgotMode ? 'Reset your password' : isRegister ? 'Register for a seamless experience' : 'Login for a seamless experience'}</p>
            </div>

            {/* Forgot Password Form */}
            {forgotMode ? (
              forgotSent ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
                  <h3 style={{ color: '#00C853', marginBottom: 8 }}>Reset Link Sent!</h3>
                  <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6 }}>
                    If this email exists in our system, a password reset link has been sent.
                  </p>
                  <button
                    onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); }}
                    style={{ marginTop: 20, background: '#00C853', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 32px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                  >
                    ← Back to Login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="auth-form-split">
                  <p style={{ color: '#475569', fontSize: 13, marginBottom: 16, textAlign: 'center' }}>
                    Enter your email address to receive a password reset link.
                  </p>
                  <div className="auth-input-wrap-row">
                    <span className="auth-row-icon">✉️</span>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" className="auth-submit-btn green" disabled={forgotLoading}>
                    {forgotLoading ? <><span className="auth-spinner" /> Sending...</> : 'Send Reset Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForgotMode(false)}
                    style={{ marginTop: 8, background: 'transparent', color: '#6b7280', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%' }}
                  >
                    ← Back to Login
                  </button>
                </form>
              )
            ) : isRegister ? (
              <form onSubmit={handleRegister} className="auth-form-split">
                <div className="auth-input-wrap-row">
                  <span className="auth-row-icon">👤</span>
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={regName}
                    onChange={e => setRegName(e.target.value)}
                    required
                  />
                </div>

                <div className="auth-input-wrap-row">
                  <span className="auth-row-icon">✉️</span>
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={regEmail}
                    onChange={e => setRegEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="auth-input-wrap-row">
                  <span className="auth-row-icon">📱</span>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={regPhone}
                    onChange={e => setRegPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    maxLength={10}
                    required
                  />
                </div>

                <div className="auth-input-wrap-row">
                  <span className="auth-row-icon">🔒</span>
                  <input
                    type={showRegPass ? 'text' : 'password'}
                    placeholder="Password (min 6 characters)"
                    value={regPassword}
                    onChange={e => setRegPassword(e.target.value)}
                    required
                  />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowRegPass(p => !p)}>
                    {showRegPass ? '🙈' : '👁️'}
                  </button>
                </div>

                <div className="auth-input-wrap-row">
                  <span className="auth-row-icon">🔒</span>
                  <input
                    type={showRegConfirm ? 'text' : 'password'}
                    placeholder="Confirm Password"
                    value={regConfirmPassword}
                    onChange={e => setRegConfirmPassword(e.target.value)}
                    required
                  />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowRegConfirm(p => !p)}>
                    {showRegConfirm ? '🙈' : '👁️'}
                  </button>
                </div>

                <div className="auth-referral-row">
                  <span className="auth-referral-label">
                    <span className="auth-row-icon">🎁</span>
                    Do you have any referral code?
                  </span>
                  <div
                    className={`auth-toggle ${hasReferral ? 'on' : ''}`}
                    onClick={() => setHasReferral(!hasReferral)}
                  >
                    <div className="auth-toggle-thumb" />
                  </div>
                </div>

                {hasReferral && (
                  <div className="auth-input-wrap-row">
                    <span className="auth-row-icon">🏷️</span>
                    <input
                      type="text"
                      placeholder="Enter Referral Code"
                      value={referralCode}
                      onChange={e => setReferralCode(e.target.value.toUpperCase())}
                    />
                  </div>
                )}

                {error && <p className="auth-error-msg">{error}</p>}

                <button type="submit" className="auth-submit-btn green" disabled={loading}>
                  {loading ? <><span className="auth-spinner" /> Processing...</> : 'Register Now'}
                </button>

                <div className="auth-switch-row">
                  <p>Already have an account?{' '}
                    <button type="button" onClick={() => { setIsRegister(false); setError(''); }}>Login</button>
                  </p>
                </div>
              </form>
            ) : (
              /* Login Form */
              <form onSubmit={handleLogin} className="auth-form-split">
                <div className="auth-input-wrap-row">
                  <span className="auth-row-icon">✉️</span>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={loginEmail}
                    onChange={e => setLoginEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="auth-input-wrap-row">
                  <span className="auth-row-icon">🔒</span>
                  <input
                    type={showLoginPass ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    required
                  />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowLoginPass(p => !p)}>
                    {showLoginPass ? '🙈' : '👁️'}
                  </button>
                </div>

                {error && <p className="auth-error-msg">{error}</p>}

                <div style={{ textAlign: 'right', marginBottom: 4 }}>
                  <button type="button"
                    onClick={() => { setForgotMode(true); setError(''); }}
                    style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: 12, cursor: 'pointer', padding: 0 }}>
                    Forgot Password?
                  </button>
                </div>

                <button type="submit" className="auth-submit-btn green" disabled={loading}>
                  {loading ? <><span className="auth-spinner" /> Processing...</> : 'Login'}
                </button>

                <div className="auth-switch-row">
                  <p>Don't have an account?{' '}
                    <button type="button" onClick={() => { setIsRegister(true); setError(''); }}>Sign Up</button>
                  </p>
                </div>
              </form>
            )}

            <hr className="auth-divider" />
            <p className="auth-terms-split">
              By continuing, you agree to the <a href="#">Terms & Conditions</a> and{' '}
              <a href="#">Privacy Policy</a> of JustService24.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
